import {
  STUDIO_BLOCKED_CAPABILITIES,
  STUDIO_BUILDER_VERSION,
  STUDIO_SUPPORTED_CAPABILITIES,
  type StudioBlockedCapabilityId,
  type StudioBuilderInput,
  type StudioCapabilityRecommendation,
  type StudioGeneratedAssistantBrief,
  type StudioSupportedCapabilityId,
} from "./studioTypes";

const CAPABILITY_PATTERNS: Record<StudioSupportedCapabilityId, RegExp[]> = {
  hours_location: [
    /\b(hour|hours|location|address|directions|parking|open|close|phone)\b/i,
  ],
  scheduling_guidance: [
    /\b(schedule|scheduling|book|booking|appointment|availability|calendar)\b/i,
  ],
  renewal_guidance: [
    /\b(renew|renewal|recert|recertification|follow-up visit|follow up visit)\b/i,
  ],
  staff_handoff: [
    /\b(staff|handoff|hand off|transfer|escalate|human|operator|agent)\b/i,
  ],
  callback_capture: [
    /\b(callback|call back|return call|follow-up call|follow up call|voicemail)\b/i,
  ],
  preview_demo: [
    /\b(preview|demo|sandbox|rehearsal|internal testing|internal test)\b/i,
  ],
};

const BLOCKED_PATTERNS: Record<StudioBlockedCapabilityId, RegExp[]> = {
  medical_advice: [
    /\b(medical advice|diagnos(?:e|is)|symptom|symptoms|treatment|clinical advice|triage)\b/i,
  ],
  dosage_guidance: [
    /\b(dosage|dose|milligram|mg\b|what should i take|what medicine|what medication)\b/i,
  ],
  live_clinic_activation: [
    /\b(go live|live deployment|auto-enable live|activate live|turn on live|production calls|live clinic|inbound receptionist|outbound scheduling)\b/i,
  ],
  booking_or_webhook_changes: [
    /\b(webhook|rewrite booking|change booking|replace webhook|change routing truth|change routing)\b/i,
  ],
  payment_or_sensitive_phi_collection: [
    /\b(card number|credit card|cvv|cvc|social security|ssn|date of birth|dob)\b/i,
  ],
};

const DEFAULT_RECOMMENDED_CAPABILITIES: StudioSupportedCapabilityId[] = [
  "hours_location",
  "scheduling_guidance",
  "staff_handoff",
  "preview_demo",
];

function normalizeOperatorDescription(value: string) {
  return String(value || "")
    .trim()
    .replace(/\s+/g, " ");
}

function uniqueOrdered<T>(values: T[]) {
  const seen = new Set<T>();

  return values.filter((value) => {
    if (seen.has(value)) {
      return false;
    }

    seen.add(value);
    return true;
  });
}

function definitionForCapability(id: StudioSupportedCapabilityId) {
  return STUDIO_SUPPORTED_CAPABILITIES.find((capability) => capability.id === id)!;
}

function blockedDefinitionForCapability(id: StudioBlockedCapabilityId) {
  return STUDIO_BLOCKED_CAPABILITIES.find((capability) => capability.id === id)!;
}

function pickAssistantName(normalizedDescription: string) {
  if (/\b(concierge|premium|white glove|high-end|luxury)\b/i.test(normalizedDescription)) {
    return "VetCan Front Desk Concierge";
  }

  if (
    /\b(renew|renewal|recert|recertification)\b/i.test(normalizedDescription) &&
    !/\b(schedule|book|appointment)\b/i.test(normalizedDescription)
  ) {
    return "VetCan Renewal Guide";
  }

  if (/\b(schedule|booking|book|appointment)\b/i.test(normalizedDescription)) {
    return "VetCan Scheduling Guide";
  }

  return "VetCan Front Desk Assistant";
}

function buildStarterTone(normalizedDescription: string) {
  const tokens = ["calm"];

  if (/\b(warm|friendly|welcoming|reassuring)\b/i.test(normalizedDescription)) {
    tokens.push("warm");
  } else {
    tokens.push("warm");
  }

  if (/\b(premium|concierge|white glove|luxury)\b/i.test(normalizedDescription)) {
    tokens.push("premium");
  }

  if (/\b(brief|concise|short|tight)\b/i.test(normalizedDescription)) {
    tokens.push("concise");
  } else {
    tokens.push("concise");
  }

  if (/\b(formal|polished|professional)\b/i.test(normalizedDescription)) {
    tokens.push("professional");
  } else {
    tokens.push("professional");
  }

  return uniqueOrdered(tokens).join(", ");
}

function getCapabilityReason(id: StudioSupportedCapabilityId) {
  switch (id) {
    case "hours_location":
      return "Front-desk requests map cleanly to hours, location, and clinic access answers.";
    case "scheduling_guidance":
      return "Booking language maps to safe scheduling guidance that preserves current routing truth.";
    case "renewal_guidance":
      return "Renewal language maps to next-step renewal guidance without creating medical scope.";
    case "staff_handoff":
      return "Every Studio assistant needs a deterministic staff handoff path.";
    case "callback_capture":
      return "Follow-up language maps to callback capture for staff review.";
    case "preview_demo":
      return "Studio remains preview-only, so internal preview and demo support stays recommended.";
  }
}

function getBlockedReason(id: StudioBlockedCapabilityId) {
  switch (id) {
    case "medical_advice":
      return "Medical advice and diagnosis remain out of scope for VetCan Studio.";
    case "dosage_guidance":
      return "Medication and dosage guidance cannot be suggested by the builder.";
    case "live_clinic_activation":
      return "Live clinic deployment cannot be enabled from Studio output.";
    case "booking_or_webhook_changes":
      return "Booking logic, routing, and webhook behavior stay outside Studio generation.";
    case "payment_or_sensitive_phi_collection":
      return "Sensitive payment or PHI collection is blocked from Studio capability recommendations.";
  }
}

function getRecommendedCapabilityIds(
  normalizedDescription: string
): StudioSupportedCapabilityId[] {
  const matchedIds = STUDIO_SUPPORTED_CAPABILITIES.filter((capability) =>
    CAPABILITY_PATTERNS[capability.id].some((pattern) =>
      pattern.test(normalizedDescription)
    )
  ).map((capability) => capability.id);

  return uniqueOrdered([
    ...(matchedIds.length > 0 ? matchedIds : DEFAULT_RECOMMENDED_CAPABILITIES),
    "staff_handoff",
    "preview_demo",
  ]);
}

function buildDescription(params: {
  assistantName: string;
  recommendedCapabilities: StudioCapabilityRecommendation[];
}) {
  const phrases = params.recommendedCapabilities
    .filter((capability) => capability.id !== "preview_demo")
    .slice(0, 3)
    .map((capability) => {
      switch (capability.id) {
        case "hours_location":
          return "answers hours and location questions";
        case "scheduling_guidance":
          return "guides approved public scheduling steps";
        case "renewal_guidance":
          return "explains renewal next steps";
        case "staff_handoff":
          return "prepares clean staff handoffs";
        case "callback_capture":
          return "captures callback requests for staff follow-up";
        default:
          return null;
      }
    })
    .filter(Boolean) as string[];

  const summary =
    phrases.length > 0
      ? phrases.join(", ").replace(/, ([^,]+)$/, ", and $1")
      : "supports safe front-desk guidance";

  return `${params.assistantName} ${summary}. It stays preview-only, deterministic, and aligned with current VetCan routing truth.`;
}

function buildStarterPrompt(params: {
  assistantName: string;
  starterTone: string;
  recommendedCapabilities: StudioCapabilityRecommendation[];
}) {
  return [
    `You are ${params.assistantName}.`,
    `Keep every reply ${params.starterTone}.`,
    `Stay within approved capabilities: ${params.recommendedCapabilities
      .map((capability) => capability.label)
      .join(", ")}.`,
    "Do not provide medical advice, recommend diagnosis or dosage, collect sensitive payment or PHI details, change booking or webhook behavior, or activate live clinic routing.",
    "When the request leaves safe front-desk scope, explain the next step briefly and hand off to staff.",
  ].join(" ");
}

function buildReadinessNotes(params: {
  recommendedCapabilities: StudioCapabilityRecommendation[];
  blockedDetections: StudioBlockedCapabilityId[];
}) {
  const notes = [
    "Output is deterministic and editable before preview.",
    "Recommendations stay bounded to current VetCan-supported capabilities.",
    "Live deployment remains blocked; internal preview and sandbox lanes only.",
  ];

  if (
    params.recommendedCapabilities.some(
      (capability) =>
        capability.id === "scheduling_guidance" ||
        capability.id === "renewal_guidance"
    )
  ) {
    notes.push(
      "Confirm the current owner-approved routing truth before using scheduling or renewal language."
    );
  }

  if (params.blockedDetections.length > 0) {
    notes.push(
      `Remove blocked requests before operator review: ${params.blockedDetections
        .map((id) => blockedDefinitionForCapability(id).label)
        .join(", ")}.`
    );
  }

  return notes;
}

export function generateStudioAssistantBrief(
  input: StudioBuilderInput
): StudioGeneratedAssistantBrief {
  const normalizedDescription = normalizeOperatorDescription(
    input.operatorDescription
  );
  const safeDescription =
    normalizedDescription ||
    "Create a premium front-desk assistant for internal preview, safe scheduling guidance, and clean staff handoffs.";
  const assistantName = pickAssistantName(safeDescription);
  const recommendedCapabilities = getRecommendedCapabilityIds(safeDescription).map(
    (id) => {
      const capability = definitionForCapability(id);

      return {
        id,
        label: capability.label,
        reason: getCapabilityReason(id),
      };
    }
  );
  const blockedCapabilities = STUDIO_BLOCKED_CAPABILITIES.map((capability) => ({
    id: capability.id,
    label: capability.label,
    reason: getBlockedReason(capability.id),
    detectedFromInput: BLOCKED_PATTERNS[capability.id].some((pattern) =>
      pattern.test(safeDescription)
    ),
  }));
  const blockedDetections = blockedCapabilities
    .filter((capability) => capability.detectedFromInput)
    .map((capability) => capability.id);
  const starterTone = buildStarterTone(safeDescription);

  return {
    version: STUDIO_BUILDER_VERSION,
    mode: "deterministic_policy",
    input: {
      operatorDescription: input.operatorDescription,
      normalizedDescription: safeDescription,
    },
    assistantName,
    description: buildDescription({
      assistantName,
      recommendedCapabilities,
    }),
    starterTone,
    starterPrompt: buildStarterPrompt({
      assistantName,
      starterTone,
      recommendedCapabilities,
    }),
    recommendedCapabilities,
    blockedCapabilities,
    readinessNotes: buildReadinessNotes({
      recommendedCapabilities,
      blockedDetections,
    }),
  };
}
